import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types'; // Import PropTypes for prop validation
import { Form, Input, Select, Radio, Space, Button, Row, Col, message } from 'antd';
import ImageUploadForm from './ImageUploadForm'; // Component for uploading the cover image
import ReactQuill from 'react-quill'; // Rich text editor component
import 'react-quill/dist/quill.snow.css'; // Styles for ReactQuill
import TagsInput from '../Inputs/TagInput'; // Custom component for handling tags
import Container from '../Container'; // Custom layout container component
import FileUpload from '../Inputs/FIleUpload'; // Custom component for handling file attachments
import { SaveOutlined } from '@ant-design/icons'; // Icon for the save button
import { updateArticleService } from '../../services/ArticleService'; // API service function
import CustomLoadingModal from '../Modal/CustomLoadingModal'; // Custom modal for loading state
import { getUserId } from '../../services/AuthService'; // Service to get the current user's ID
import { useNavigate } from 'react-router-dom';

const { Option } = Select;

// Constants for visibility options and modal messages
const VISIBILITY_PUBLIC = 'PUBLIC';
const VISIBILITY_PRIVATE = 'PRIVATE';
const MSG_UPDATING = 'Updating article...';
const MSG_UPDATE_SUCCESS = 'Article updated successfully';
const MSG_UPDATE_ERROR = 'Error updating article';

/**
 * @component EditArticleForm
 * @description A form component for editing an existing article.
 * It includes fields for title, content, category, sub-category, tags, visibility,
 * cover image, and attachments. It handles fetching initial data, updating state,
 * and submitting changes via an API service.
 *
 * @param {object} props - Component props.
 * @param {Array} props.categories - List of available categories.
 * @param {Array} props.subCategories - List of available sub-categories.
 * @param {object} props.initialValues - Initial data for the article being edited.
 * @param {Function} props.onSubmit - Callback function triggered after successful submission.
 * @returns {JSX.Element} The rendered form component.
 */
const EditArticleForm = ({ categories = [], subCategories = [], initialValues, onSubmit }) => {
  // Ant Design form instance
  const [form] = Form.useForm();

  const navigate = useNavigate();
  // --- State Variables ---
  // State for the article's cover image URL
  const [articleImage, setArticleImage] = useState(initialValues?.coverUrl || '');
  // State for the rich text content (managed by ReactQuill)
  const [content, setContent] = useState(initialValues?.content || '');
  // State for the article's visibility (PUBLIC or PRIVATE)
  const [privacy, setPrivacy] = useState(initialValues?.visibility || VISIBILITY_PUBLIC);
  // State for sub-categories filtered based on the selected category
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  // State for the list of article attachments { id?, label, url }
  const [attachments, setAttachments] = useState(initialValues?.ArticleAttachment || []);
  // State to track if the form is currently submitting data
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State to control the visibility of the loading/status modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  // State for the message displayed in the loading/status modal
  const [modalMessage, setModalMessage] = useState(MSG_UPDATING);

  const [roleType, setRoleType] = useState(null); // State for role type



  // --- Effects ---
  /**
   * @effect
   * Populates the form with initial values when `initialValues` prop changes.
   * Sets up initial state for content, image, attachments, privacy, and filters sub-categories.
   */
  useEffect(() => {
    // Guard clause: Do nothing if initialValues are not yet available
    if (!initialValues) {
      return;
    }
    const role = localStorage.getItem('roleType'); // Get the role type from local storage
    setRoleType(role); // Set the role type state

    const initialCategory = categories.find(cat => cat.id == initialValues?.categoryId);
    const iSub = subCategories.find(cat => cat.id == initialValues?.subcategoryId);
    console.log("Messaage",initialCategory);
    console.log("categoryId",initialValues.categoryId);
    console.log('subcategory',iSub);

    // Format initial values for the form (e.g., splitting tags string into array)
    const formattedValues = {
      ...initialValues,
      // Split comma-separated tags string into an array for the TagsInput component
      tags: initialValues.tags ? initialValues.tags.split(', ') : [],
      // Ensure categoryId and subCategoryId are correctly passed if they exist
      categoryId: initialCategory?.id,
      subCategoryId: iSub?.id,
    };

    console.log(initialValues);
    

    // Set form fields using the formatted values
    form.setFieldsValue(formattedValues);

    // Set component state based on initialValues
    setContent(initialValues.content || '');
    setArticleImage(initialValues.coverUrl || '');
    // Assuming initialValues.ArticleAttachment is the array of attachments
    setAttachments(initialValues.ArticleAttachment || []);
    setPrivacy(initialValues.visibility || VISIBILITY_PUBLIC);

    // Filter sub-categories based on the initial category ID
    if (initialValues.categoryId && Array.isArray(subCategories)) {
      const filtered = subCategories.filter(
        (subCategory) => subCategory.parentId == initialValues.categoryId // Use loose equality in case types differ (e.g., number vs string)
      );
      setFilteredSubCategories(filtered);
    }
  }, [initialValues, form, subCategories]); // Dependencies: Rerun when these change

  // --- Handlers ---
  /**
   * @callback handleImageUpload
   * @description Updates the articleImage state when a new image is uploaded.
   * Wrapped in useCallback for potential performance optimization if ImageUploadForm is memoized.
   * @param {string} imageUrl - The URL of the uploaded image.
   */
  const handleImageUpload = useCallback((imageUrl) => {
    setArticleImage(imageUrl);
    // Also update the form field value if needed, assuming 'ArticleImage' is the field name used in final submit
    // form.setFieldsValue({ ArticleImage: imageUrl }); // --> This seems handled in handleSubmit merger now.
  }, []); // No dependencies needed


  /**
   * @callback handleArticleSubmit
   * @description Handles the actual data formatting and API call to update the article.
   * Manages loading state, modal display, and success/error feedback.
   * @param {object} values - Form values collected by Ant Design's Form, merged with other state.
   */
  const handleArticleSubmit = async (values) => {
    setIsSubmitting(true);
    setIsModalVisible(true);
    setModalMessage(MSG_UPDATING);

    try {
        // --- Attachment Handling Logic ---
        // Get IDs of attachments that were initially present
        const initialAttachmentIds = initialValues?.ArticleAttachment?.map(a => a.id) || [];
        // Current attachments state (mix of existing with IDs and new without IDs)
        const currentAttachments = attachments;

        // Identify existing attachments (those that have an ID)
        const existingAttachments = currentAttachments.filter(a => a.id);
        // Identify new attachments (those without an ID)
        const newAttachments = currentAttachments.filter(a => !a.id);

        // Identify attachments to delete: Present in initial list but NOT in current list
        const attachmentsToDelete = initialAttachmentIds.filter(id =>
            !currentAttachments.some(a => a.id === id)
        );
        // --- End Attachment Handling ---

        // --- User ID ---
        // Get the user ID from the authentication service
        const userId = getUserId(); // Assuming this returns the user ID (e.g., number or string)
        if (!userId) {
            console.error("User ID not found. Cannot update article.");
            message.error("Authentication error. Could not identify user.");
            throw new Error("User ID is missing"); // Prevent submission without user ID
        }
        const parsedUserId = parseInt(userId, 10); // Ensure it's a number (base 10)
         if (isNaN(parsedUserId)) {
             console.error("Invalid User ID format:", userId);
             message.error("Invalid user data.");
             throw new Error("Invalid User ID format");
         }
         console.log('formData',values);

        // --- Prepare Payload for API ---
        // This structure suggests a Prisma-like backend expecting specific operations (set, connect, create, update, delete)
        const articleData = {
            // Use 'set' for simple field updates
            title: { set: values.title },
            content: { set: content }, // Content from ReactQuill state
            tags: { set: values.tags.join(', ') }, // Join tags array back into string
            visibility: { set: privacy }, // Privacy from state
            coverUrl: { set: values.ArticleImage }, // Image URL from state via merged values
            // Properly wrap status in a 'set' operation
            status: { set: roleType === "ADMIN" ? "APPROVED" : "DRAFT" },
            // Use 'connect' to link to existing related records
            User: { connect: { id: parsedUserId } }, // Connect to the author User
            Category: { connect: { id: parseInt(values.categoryId) } },
            SubCategory: { connect: { id: parseInt(values.subCategoryId) } },

            // Handle nested mutations for attachments
            ArticleAttachment: {
              
                // Create new attachments
                create: newAttachments.map(attachment => ({
                    label: attachment.label,
                    url: attachment.url
                }))
            }
        };

        console.log("Submitting Article Data:", articleData);

        // Call the API service to update the article
        await updateArticleService(initialValues.id, articleData);

        // --- Success Handling ---
        setModalMessage(MSG_UPDATE_SUCCESS);
        // Optional: Keep modal open for a short duration to show success message
        await new Promise(resolve => setTimeout(resolve, 1500)); // 1.5 second delay
        message.success(MSG_UPDATE_SUCCESS);
        await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulating 3 seconds delay
        navigate('/admin/articles'); // Redirect to the articles page after creation

       
        if (onSubmit) {
            onSubmit(); // Call the callback function provided by the parent component
        }

    } catch (error) {
        // --- Error Handling ---
        console.error("Error updating article:", error);
        setModalMessage(MSG_UPDATE_ERROR);
         // Optional: Keep modal open for a short duration to show error message
        await new Promise(resolve => setTimeout(resolve, 2500)); // 2.5 second delay
        message.error(MSG_UPDATE_ERROR);

    } finally {
        // --- Cleanup ---
        setIsSubmitting(false);
        setIsModalVisible(false); // Close modal regardless of success/error after delay
    }
  };

  /**
   * @callback handleSubmit
   * @description Intermediate handler called by Ant Design Form's `onFinish`.
   * Merges form values with state values (content, image) before calling `handleArticleSubmit`.
   * @param {object} formValues - Values directly from the Ant Design Form fields.
   */
  const handleSubmit = (formValues) => {
    // Merge values from form state with values from component state (ReactQuill, ImageUpload)
    const completeFormData = {
        ...formValues,
        content, // Add content from ReactQuill state
        ArticleImage: articleImage // Add image URL from state
    };
    handleArticleSubmit(completeFormData); // Pass merged data to the final submission handler
  };

  /**
   * @callback handleTagsChange
   * @description Updates the 'tags' field in the Ant Design form state.
   * Wrapped in useCallback.
   * @param {string[]} tags - The current array of tags.
   */
  const handleTagsChange = useCallback((tags) => {
    form.setFieldsValue({ tags });
  }, [form]); // Depends on `form` instance

  /**
   * @callback handleCategoryChange
   * @description Filters sub-categories when the main category changes and resets the sub-category field.
   * Wrapped in useCallback.
   * @param {string|number} categoryId - The ID of the selected category.
   */
  const handleCategoryChange = useCallback((categoryId) => {
    if (Array.isArray(subCategories)) {
        const filtered = subCategories.filter(
            (subCategory) => subCategory.parentId == categoryId // Use loose equality
        );
        setFilteredSubCategories(filtered);
        console.log('categoryId',categoryId);
        console.log('datasub',subCategories);
        console.log("subcategory",filtered);
    } else {
        setFilteredSubCategories([]); // Clear if subCategories isn't an array
    }
    // Reset the sub-category selection when the main category changes
    form.setFieldsValue({ subCategoryId: undefined });
  }, [subCategories, form]); // Depends on `subCategories` list and `form` instance

  /**
   * @callback handleAttachmentsChange
   * @description Updates the attachments state when the FileUpload component reports changes.
   * Wrapped in useCallback.
   * @param {object[]} newAttachments - The updated list of attachment objects.
   */
  const handleAttachmentsChange = useCallback((newAttachments) => {
    setAttachments(newAttachments);
  }, []); // No dependencies needed

  /**
   * @callback onPrivacyChange
   * @description Updates the privacy state when the radio button selection changes.
   * @param {object} e - The event object from the Radio group.
   */
  const onPrivacyChange = (e) => {
    setPrivacy(e.target.value);
  };

  // --- Render ---
  return (
    <Container>
      {/* Ant Design Form */}
      <Form
        form={form} // Pass the form instance
        layout="vertical" // Use vertical layout for labels above inputs
        onFinish={handleSubmit} // Handler called when form validation succeeds
        // initialValues={initialValues} // initialValues are now set via useEffect and form.setFieldsValue
        // Note: Setting initialValues here AND in useEffect with setFieldsValue can sometimes cause race conditions or double updates.
        // Using useEffect provides more control for complex initialization logic like tag splitting and state setting.
      >
        {/* Section: Cover Image Upload */}
        {/* Form.Item without 'name' acts as a layout wrapper */}
        <Form.Item label="Cover Image" style={{ marginTop: '20px' }}>
          <ImageUploadForm
            onImageUpload={handleImageUpload}
            initialImage={articleImage} // Pass current image URL to the uploader
          />
          {/* Display preview of the uploaded/initial image */}
          {
          // You might not need this explicit img tag if ImageUploadForm shows a preview
          articleImage && (
            <img
              src={articleImage}
              alt="Article Cover"
              style={{ maxWidth: '100%', marginTop: 10, }}
            />
          )
          }
          
        </Form.Item>

        {/* Section: Title */}
        <Form.Item
          name="title"
          label="Article Title"
          rules={[{ required: true, message: 'Please enter the article title!' }]}
        >
          <Input placeholder="Enter article title" />
        </Form.Item>

        {/* Section: Content (Rich Text Editor) */}
        {/* ReactQuill is not a standard Form.Item input, so manage its value via state */}
        <Form.Item
          label="Content"
          required // Mark label as required
          // Add custom validation logic if needed, possibly using form.validateFields() before submit
        >
          <ReactQuill
            theme="snow"
            value={content}
            onChange={setContent} // Update state directly
            style={{ backgroundColor: 'white' }} // Ensure contrast if needed
          />
           {/* Basic validation feedback could be added manually here if needed */}
        </Form.Item>

        {/* Section: Categories & Sub-Categories */}
        <Row gutter={16}>
        <Col xs={24} sm={12}>
        {Array.isArray(categories) && categories.length > 0 && (
                            <Form.Item name="categoryId" label="Category" rules={[{ required: true }]}>
                                <Select 
                                    placeholder="Select category" 
                                    onChange={handleCategoryChange}
                                    defaultValue={initialValues?.categoryId}
                                   // value={initialValues?.categoryId}
                                >
                                    {categories.map((category) => (
                                        <Option key={category.id} value={category.id}>
                                            {category.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            )}
  {/* Only render category selector if categories are available */}
  
</Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="subCategoryId"
              label="Sub-category"
              rules={[{ required: true, message: 'Please select a sub-category!' }]}
            >
              <Select
                placeholder="Select sub-category"
                disabled={filteredSubCategories.length === 0} // Disable if no category selected or no sub-categories exist
                allowClear
              >
                {filteredSubCategories.map((subCategory) => (
                  <Option key={subCategory.id} value={subCategory.id}>
                    {subCategory.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Section: Visibility & Tags */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              // name="visibility" // Name is not strictly needed if value managed by state 'privacy' & onChange updates it
              label="Visibility"
              rules={[{ required: true, message: 'Please set visibility!' }]}
            >
              {/* Controlled Radio Group using 'privacy' state */}
              <Radio.Group onChange={onPrivacyChange} value={privacy}>
                <Radio value={VISIBILITY_PUBLIC}>Public</Radio>
                <Radio value={VISIBILITY_PRIVATE}>Private</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Tags"
              name="tags" // Connects to Ant Design Form state
              rules={[{ required: true, message: 'Please add at least one tag!' }]}
            >
              {/* Custom TagsInput component, updates form via `handleTagsChange` */}
              <TagsInput
                onChange={handleTagsChange}
                initialTags={form.getFieldValue('tags')} // Pass initial/current tags from form state
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Section: Attachments */}
        <Form.Item label="Attachments">
           {/* Custom FileUpload component, manages its own state and reports via `handleAttachmentsChange` */}
          <FileUpload
            onAttachmentsChange={handleAttachmentsChange}
            initialAttachments={attachments} // Pass current attachments state
          />
        </Form.Item>

        {/* Section: Submit Button */}
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit" // Triggers form's onFinish
            size="large"
            icon={<SaveOutlined />}
            loading={isSubmitting} // Show loading indicator on button when submitting
            style={{
              // Consider moving styles to a CSS module or styled-component for better separation
              background: 'black',
              color: 'white',
              borderRadius: '8px', // Slightly reduced border-radius
              padding: '10px 20px', // Adjusted padding
              fontSize: '16px',
              border: 'none', // Remove default border
              minWidth: '150px', // Ensure minimum width
            }}
          >
            Save Changes
          </Button>
        </Form.Item>
      </Form>

      {/* Loading/Status Modal */}
      <CustomLoadingModal
        visible={isModalVisible}
        loading={isSubmitting && modalMessage === MSG_UPDATING} // Show spinner only during update phase
        message={modalMessage} // Display current status message
        // Optional: Add onCancel handler if the modal should be closable manually
      />
    </Container>
  );
};

// PropTypes for component documentation and validation
EditArticleForm.propTypes = {
  /** Array of category objects, typically { id: number|string, name: string } */
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  /** Array of sub-category objects, typically { id: number|string, name: string, parentId: number|string } */
  subCategories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    parentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })),
  /** Initial data for the article being edited */
  initialValues: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string,
      content: PropTypes.string,
      coverUrl: PropTypes.string,
      tags: PropTypes.string, // Comma-separated string initially
      visibility: PropTypes.oneOf([VISIBILITY_PUBLIC, VISIBILITY_PRIVATE]),
      categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      subCategoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ArticleAttachment: PropTypes.arrayOf(PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // ID might be missing for new ones initially
          label: PropTypes.string,
          url: PropTypes.string,
      }))
  }),
  /** Callback function executed after a successful article update */
  onSubmit: PropTypes.func,
};

export default EditArticleForm;